/* eslint-disable @typescript-eslint/indent */
/*
   Note: This Thermometer component has a lot of the styling inverted due to its vertical
   orientation and use of rotation transforms.
   Be sure to carefully review any changes to the styling of this component to avoid unexpected
   effects and ensure visual consistency.
*/

// Core
import styled from 'styled-components';

// Libraries
import { Progress as ProgressAntD } from 'antd';

// Config
import { BREAKPOINTS } from '../../config/screen';

// Plugins
import { formatNumber } from '../../plugins/general';

// Types
import { Theme, ThemedProps } from '../../types/theme';
import { Thresholds } from '../../types/global';

// Component
import { VibrationThermometerProps } from './VibrationThermometer.types';

/*
  Generates CSS styles to display threshold ticks on the vibration thermometer
  @param threshold receives a record of threshold values
  @param theme receives the theme object
  @param running indicates if the thermometer is running
  @param hasValidationError indicates if there's a threshold validation error
  @returns a string of CSS styles
*/
const showThresholdTicks = (
  threshold: Thresholds,
  theme: Theme,
  running: boolean,
  hasValidationError: boolean = false,
): string => {
  const mapStepColor = (formatedThreshold: string, backgroundColor: string): string => `
    .ant-progress-steps-item:nth-child(${formatedThreshold}) {
      height: 12px !important;
      width: 2px !important;
      background-color: ${
        running && !hasValidationError
          ? backgroundColor
          : theme.color.vibrationThermometer.offThresholdTickBackgroundColor
      };
    };
  `;

  return `
    ${mapStepColor(
      formatNumber(threshold.highWarning),
      theme.color.vibrationThermometer.tickHighWarningBackgroundColor,
    )}
    ${mapStepColor(
      formatNumber(threshold.highAlarm),
      theme.color.vibrationThermometer.tickHighAlarmBackgroundColor,
    )}
    ${mapStepColor(
      formatNumber(threshold.lowWarning),
      theme.color.vibrationThermometer.tickLowWarningBackgroundColor,
    )}
    ${mapStepColor(
      formatNumber(threshold.lowAlarm),
      theme.color.vibrationThermometer.tickLowAlarmBackgroundColor,
    )}
  `;
};

/*
  Styled component for the VibrationThermometer element
  @param value receives the current thermometer value
  @param thresholds receives the threshold object
  @param running indicates if the thermometer is running
  @param theme receives the theme object
*/
export const Element = styled.div<Pick<
  VibrationThermometerProps, 'value' | 'thresholds' | 'running'> & ThemedProps
>`
  height: 220px;
  margin: 10px 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;

  /* ******* VALUES STYLES ******* */
  .max-value-container,
  .min-value-container {
    position: absolute;
    font-family: ${({ theme }) => theme.font.regular};
    color: ${({ theme }) => theme.color.vibrationThermometer.minAndMaxValueTextColor};
    transform: rotate(90deg);
    font-size: 0.75rem;
    white-space: nowrap;
    pointer-events: none;
  };

  .max-value-container {
    right: -18px;
    top: 50%;
    transform: rotate(90deg) translateX(-50%);
  };

  .min-value-container {
    left: -12px;
    top: 0;
    transform: rotate(90deg) translateX(-50%);
  };

  /* ******* VALUE CONTAINER ******* */
  .value-container {
    transform: rotate(90deg);
    font-family: ${({ theme }) => theme.font.regular};
    margin-top: 6px;
    font-size: 18px;
    white-space: nowrap;
  };

  @media (min-width: ${BREAKPOINTS.sm}px) and (max-width: ${BREAKPOINTS.md}px) {
    height: 189px;

    .max-value-container,
    .min-value-container {
      font-size: 11px;
    };

    .value-container {
      margin-top: 7px;
      font-size: 16px;
    };
  };

  @media (min-width: 376px) and (max-width: ${BREAKPOINTS.sm - 1}px) {
    height: 178px;

    .max-value-container,
    .min-value-container {
      font-size: 10.5px;
    };

    .value-container {
      margin-top: 6.5px;
      font-size: 15px;
    };
  };

  @media (min-width: ${BREAKPOINTS.xs}px) and (max-width: 375px) {
    height: 168px;

    .max-value-container,
    .min-value-container {
      font-size: 10px;
    };

    .max-value-container {
      right: -14px;
    };

    .min-value-container {
      left: -7px;
    };

    .value-container {
      margin-top: 6px;
      font-size: 14px;
    };
  };
`;

/* Styled component for the container of the VibrationThermometer */
export const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  flex-direction: column;
  height: 145px;
  position: relative;

  @media (min-width: ${BREAKPOINTS.sm}px) and (max-width: ${BREAKPOINTS.md}px) {
    height: 130px;
  };

  @media (min-width: 376px) and (max-width: ${BREAKPOINTS.sm - 1}px) {
    height: 122px;
  };

  @media (min-width: ${BREAKPOINTS.xs}px) and (max-width: 375px) {
    height: 115px;
  };
`;

/* Styled component for the container of progress indicators */
export const ProgressIndicatorsContainer = styled.div`
  transform: rotate(270deg);
`;

/* Styled component for the AntD progress bar */
export const ThermometerAntD = styled(ProgressAntD)`
  position: relative;
  top: 10px;
  bottom: 0;
  right: 0;

  /* ******* PROGRESS ******* */
  .ant-progress-small {
    width: 10px;
  };

  /* ******* PROGRESS STEPS ******* */
  .ant-progress-steps-item {
    width: 1px;
  };

  @media (min-width: ${BREAKPOINTS.sm}px) and (max-width: ${BREAKPOINTS.md}px) {
    .ant-progress-small {
      width: 9px !important;
    };
    .ant-progress, .ant-progress-outer, .ant-progress-inner {
      width: 153px !important;
    };
  };

  @media (min-width: 376px) and (max-width: ${BREAKPOINTS.sm - 1}px) {
    .ant-progress-small {
      width: 8px !important;
    };
    .ant-progress, .ant-progress-outer, .ant-progress-inner {
      width: 135px !important;
    };
    .ant-progress-steps-item {
      width: 0.9px !important;
    };
  };

  @media (min-width: ${BREAKPOINTS.xs}px) and (max-width: 375px) {
    .ant-progress-small {
      width: 7px !important;
    };
    .ant-progress, .ant-progress-outer, .ant-progress-inner {
      width: 120px !important;
    };
    .ant-progress-steps-item {
      width: 0.8px !important;
    };
  };
`;

/*
  Styled component for the steps of the thermometer
  @param threshold receives a record of threshold values
  @param running indicates if the thermometer is running
  @param hasValidationError indicates if there's a threshold validation error
*/
export const StepsThermometer = styled.div<{
  threshold: Thresholds,
  running: boolean,
  hasValidationError?: boolean,
}>`
  display: flex;
  position: absolute;
  top: 20px;
  left: 0;
  bottom: 0;
  right: 0;

  /* ******* PROGRESS STEPS ITEM ******* */
  .ant-progress-steps-item {
    position: relative;
    width: max-content;
    margin-top: 3px;

    span {
      position: absolute;
      top: 120px;
      right: 0;
      font-size: 10px;
      transform: rotate(90deg);
    };
  };

  ${({
    threshold,
    theme,
    running,
    hasValidationError,
  }) => showThresholdTicks(threshold, theme, running, hasValidationError || false)};

  /* ******* PROGRESS STEPS OUTER ******* */
  .ant-progress-steps-outer {
    align-items: start;
  };

  @media (min-width: ${BREAKPOINTS.sm}px) and (max-width: ${BREAKPOINTS.md}px) {
    left: calc(50% - 76.5px);
    right: auto;
    width: 153px;
    .ant-progress, .ant-progress-steps-outer {
      width: 153px !important;
    };
  };

  @media (min-width: 376px) and (max-width: ${BREAKPOINTS.sm - 1}px) {
    left: calc(50% - 67.5px);
    right: auto;
    width: 135px;
    .ant-progress, .ant-progress-steps-outer {
      width: 135px !important;
    };
    .ant-progress-steps-item {
      width: 0.9px !important;
    };
  };

  @media (min-width: ${BREAKPOINTS.xs}px) and (max-width: 375px) {
    left: calc(50% - 60px);
    right: auto;
    width: 120px;
    .ant-progress, .ant-progress-steps-outer {
      width: 120px !important;
    };
    .ant-progress-steps-item {
      width: 0.8px !important;
    };
  };
`;

/* Styled component for the progress thermometer */
export const ProgressThermometer = styled.div`
  width: 170px;
  height: 60px;
  
  /* Custom styling for vertical orientation */
  .ant-progress-inner, .ant-progress-bg {
    border-radius: 4px !important;
    height: 14px !important;
    width: 171px;
  };
 
  .ant-progress-inner {
    background-color: ${({ theme }) => theme.color.vibrationThermometer.progressInnerBackgroundColor} !important;
    position: relative;
    overflow: visible !important;
  };

  .ant-progress-bg {
    border-radius: 4px;
  };

  @media (min-width: ${BREAKPOINTS.sm}px) and (max-width: ${BREAKPOINTS.md}px) {
    width: 153px;
    height: 54px;
    .ant-progress, .ant-progress-outer, .ant-progress-inner {
      width: 153px !important;
    };
  };

  @media (min-width: 376px) and (max-width: ${BREAKPOINTS.sm - 1}px) {
    width: 135px;
    height: 47px;
    .ant-progress, .ant-progress-outer, .ant-progress-inner {
      width: 135px !important;
    };
  };

  @media (min-width: ${BREAKPOINTS.xs}px) and (max-width: 375px) {
    width: 120px;
    height: 42px;
    .ant-progress, .ant-progress-outer, .ant-progress-inner {
      width: 122px !important;
    };
  };

  /* Apply gradient based on threshold state */
  &.success .ant-progress-bg {
    background: linear-gradient(
      to left,
      ${({ theme }) => theme.color.vibrationThermometer.successGradientColor1} 0%,
      ${({ theme }) => theme.color.vibrationThermometer.successGradientColor2} 50%,
      ${({ theme }) => theme.color.vibrationThermometer.successGradientColor3} 125%
    ) !important;
  };

  &.warning .ant-progress-bg {
    background: linear-gradient(
      to left,
      ${({ theme }) => theme.color.vibrationThermometer.warningGradientColor1} 0%,
      ${({ theme }) => theme.color.vibrationThermometer.warningGradientColor2} 50%,
      ${({ theme }) => theme.color.vibrationThermometer.warningGradientColor3} 125%
    ) !important;
  };

  &.alarm .ant-progress-bg {
    background: linear-gradient(
      to left,
      ${({ theme }) => theme.color.vibrationThermometer.alarmGradientColor1} 0%,
      ${({ theme }) => theme.color.vibrationThermometer.alarmGradientColor2} 50%,
      ${({ theme }) => theme.color.vibrationThermometer.alarmGradientColor3} 125%
    ) !important;
  };

  &.off .ant-progress-bg {
    background: ${({ theme }) => theme.color.vibrationThermometer.offProgressBackgroundColor} !important;
  };
`;

/* Styled component for the text container */
export const TextContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: -5px;

  @media (min-width: 376px) and (max-width: ${BREAKPOINTS.sm - 1}px) {
    margin-bottom: -3px;
  };

  @media (min-width: ${BREAKPOINTS.xs}px) and (max-width: 375px) {
    margin-bottom: -2px;
  };
`;
